import { Clock, MonitorSmartphone, Wallet } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { PrimaryButton, SecondaryButton } from "@/components/shared/Buttons";
import { StaggerGroup, StaggerItem } from "@/components/shared/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { getByCategory, detailPath } from "@/lib/programsData";

const ListCard = ({ p, i }) => (
  <StaggerItem className={`flex flex-col overflow-hidden rounded-2xl border p-7 ${p.highlight ? "border-orange/50 bg-navy-800" : "border-white/10 bg-white/[0.03]"}`}>
    {p.highlight && (
      <span className="mb-4 w-fit rounded-full bg-orange px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">Most Popular</span>
    )}
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-orange">
      <Clock className="h-3.5 w-3.5" /> {p.duration}
    </div>
    <h3 className="mt-3 font-heading text-2xl leading-snug text-white">{p.name}</h3>
    <p className="mt-3 text-sm leading-relaxed text-white/65">{p.homeSummary}</p>
    <div className="mt-5 flex flex-wrap gap-2 text-xs text-white/60">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3 py-1"><MonitorSmartphone className="h-3.5 w-3.5" /> {p.mode}</span>
    </div>
    <div className="mt-5 flex items-center gap-2 border-t border-white/10 pt-5">
      <Wallet className="h-5 w-5 text-orange" />
      <div>
        <div className="font-heading text-xl text-white">{p.fee}</div>
        <div className="text-xs text-white/50">{p.feeNote}</div>
      </div>
    </div>
    <div className="mt-6 flex flex-col gap-3 sm:flex-row">
      <SecondaryButton to={detailPath(p)} onDark className="flex-1" testId={`programs-view-${i}`}>View Details</SecondaryButton>
      <PrimaryButton to="/apply" className="flex-1" testId={`programs-apply-${i}`}>Apply Now</PrimaryButton>
    </div>
  </StaggerItem>
);

export default function Programs() {
  useSeo("Programs", "Explore ASAI's industry-oriented AI programs — 3-month internship, 6-month industrial training and a 1-year job-oriented program in AI, ML, Cloud and more.");
  const programs = getByCategory("program");

  return (
    <div data-testid="page-programs">
      <PageHeader
        overline="Programs"
        titleLines={["Industry-oriented", "AI programs"]}
        description="Choose a program that matches your goals — from a focused internship to a complete one-year job-oriented pathway."
      />

      <section className="bg-navy py-20 md:py-28">
        <div className="container-asai">
          <StaggerGroup className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {programs.map((p, i) => (
              <ListCard key={p.slug} p={p} i={i} />
            ))}
          </StaggerGroup>
        </div>
      </section>

      <section className="bg-lightgrey py-20 md:py-24">
        <div className="container-asai flex flex-col items-start gap-8">
          <SectionHeading
            overline="Looking for a free option?"
            title="Explore our free internships"
            description="We also run selection-based free internships in UPI and in AI & ML."
          />
          <div className="flex flex-wrap gap-3">
            <PrimaryButton to="/internships" withArrow testId="programs-see-internships">See Internships</PrimaryButton>
            <SecondaryButton to="/contact" testId="programs-counselling-btn">Book Free Counselling</SecondaryButton>
          </div>
        </div>
      </section>
    </div>
  );
}
